import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useQuoteCart } from '../context/QuoteCartContext';

export default function QuoteDrawer() {
  const { t } = useTranslation();
  const { items, remove, clear, open, setOpen } = useQuoteCart();
  const [form, setForm] = useState({
    company_name: '', contact_name: '', country: '', email: '', phone: '',
    estimated_quantity: '', notes: '',
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.country || !form.email) {
      toast.error('Please fill company, country and email');
      return;
    }
    setSending(true);
    try {
      await api.post('/quote-requests', {
        ...form,
        product_ids: items.map(i => i.id),
      });
      toast.success(t('quote.success'));
      clear();
      setForm({ company_name: '', contact_name: '', country: '', email: '', phone: '', estimated_quantity: '', notes: '' });
      setOpen(false);
    } catch (err) {
      toast.error('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-50 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setOpen(false)}
      />
      <aside
        data-testid="quote-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[520px] bg-[#FBF8F3] z-50 shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] overflow-y-auto ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-8 md:p-12">
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-3">Made in Italy</div>
              <h2 className="font-serif text-3xl md:text-4xl leading-none">{t('quote.title')}</h2>
              <p className="mt-3 text-sm text-[#3D2817]/60">{t('quote.subtitle')}</p>
            </div>
            <button data-testid="quote-drawer-close" onClick={() => setOpen(false)} className="p-2 hover:bg-[#F2ECE0] transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {items.length > 0 && (
            <div className="mb-8 pb-6 border-b border-[#E5D9C8]">
              <div className="text-xs uppercase tracking-widest text-[#3D2817]/60 mb-3">{t('quote.selected')} ({items.length})</div>
              <ul className="space-y-2">
                {items.map(item => (
                  <li key={item.id} className="flex items-center justify-between gap-4 py-2">
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-[#3D2817]/50">{item.sku}</div>
                    </div>
                    <button data-testid={`quote-remove-${item.id}`} onClick={() => remove(item.id)} className="text-[#3D2817]/40 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('quote.company')} *</label>
              <input data-testid="quote-input-company" required className="underline-input" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('quote.contact')}</label>
                <input data-testid="quote-input-contact" className="underline-input" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('quote.country')} *</label>
                <input data-testid="quote-input-country" required className="underline-input" value={form.country} onChange={e => setForm({...form, country: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('quote.email')} *</label>
                <input data-testid="quote-input-email" required type="email" className="underline-input" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('quote.phone')}</label>
                <input data-testid="quote-input-phone" className="underline-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('quote.quantity')}</label>
              <input data-testid="quote-input-quantity" placeholder="e.g. 500 kg / 20 boxes" className="underline-input" value={form.estimated_quantity} onChange={e => setForm({...form, estimated_quantity: e.target.value})} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('quote.notes')}</label>
              <textarea data-testid="quote-input-notes" rows={4} className="underline-input resize-none" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
            </div>

            <button
              data-testid="quote-submit"
              disabled={sending}
              className="w-full mt-8 bg-[#3D2817] hover:bg-[#C5A059] text-white font-sans text-sm uppercase tracking-[0.25em] py-4 flex items-center justify-center gap-3 transition-all duration-500 hover:-translate-y-0.5"
            >
              <Send className="w-4 h-4" />
              {sending ? '...' : t('quote.submit')}
            </button>
          </form>
        </div>
      </aside>
    </>
  );
}
